import { memo, useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
// Import Types
import type { DeviceTabType, DropdownOption } from 'types';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import { selectPartnerLanguages } from 'store/slices/partnerLanguages';
// Import Components
import TemplateInfoTab from './TemplateInfoTab';
import RadioTabGroup from 'components/common/RadioTabGroup';
// Import UI Components
import { BusyLoader, Tabs, Tab, Tooltip } from '@geneui/components';
// Import Helpers
import { getPartnerLanguageList, ignoreCanceledError } from 'services/Helpers';
// Import Services
import { UtilsHttpService } from 'services/http';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Enums
import { TemplateTypeEnum, DeviceTypeEnum, DisplayModeEnum } from 'enums';
// Import Constants
import { l, TemplateTypesLabels, DeviceTabInfos, ModeTabInfos } from 'constants/common';
// Import Context
import { useVersionHistoryContext } from 'components/VersionHistory';

const TemplateInfo = (props) => {
    const { defaultTemplateId, defaultBody, defaultSubject, childItemsInfo, getTemplateInfo, deliveryMethod } = props;
    const { t } = useTranslation();

    const dispatch = useAppDispatch();

    const { doGetRequest } = useRequest();

    const [activeDeviceTab, setActiveDeviceTab] = useState<DeviceTabType>(DeviceTypeEnum.Desktop);
    const [displayModeTab, setDisplayModeTab] = useState(DisplayModeEnum.LightMode);
    const [activeLanguageTab, setActiveLanguageTab] = useState(defaultTemplateId);
    const [urlTypesData, setUrlTypesData] = useState<DropdownOption<string>[]>([]);
    const [isVisibleSubjectField, setIsVisibleSubjectField] = useState(false);
    const [isRequestedTabsData, setIsRequestedTabsData] = useState(
        childItemsInfo.reduce(
            (accumulator, item) => ({
                ...accumulator,
                [item.TemplateId]: false,
            }),
            {},
        ),
    );

    const { pointInTime } = useVersionHistoryContext();

    const { getPartnerLanguage, getPopUpComparisons } = useCancelableRequests({
        getPartnerLanguage: UtilsHttpService.getPartnerLanguage,
        getPopUpComparisons: UtilsHttpService.getPopUpComparisons,
    });

    const partnerLanguages = useAppSelector(selectPartnerLanguages);

    const childItemsAdditionalInfo = useMemo(() => {
        if (isEmpty(partnerLanguages)) return [];

        return childItemsInfo.map((item) => {
            const name = `${item.Language.toUpperCase()} - ${partnerLanguages[item.Language]?.Name}`;
            return {
                ...item,
                languageCode: item.Language.toUpperCase(),
                languageName: name,
                tooltipText: name,
            };
        });
    }, [childItemsInfo, partnerLanguages]);

    const { dataTabs } = useRef({
        dataTabs: childItemsInfo.reduce(
            (accumulator, item) => ({
                ...accumulator,
                [item.TemplateId]: { subject: null, body: null, isBusy: true },
            }),
            {},
        ),
    }).current;

    const [tabsData, setTabsData] = useState(dataTabs);

    const isVisibleDeviceTabs = [
        TemplateTypesLabels.Viber,
        TemplateTypesLabels.Hoory,
        TemplateTypesLabels.Telegram,
    ].includes(deliveryMethod);

    const handleTabChange = (templateId) => {
        setActiveLanguageTab(+templateId);
        if (+templateId === +defaultTemplateId) return;

        if (!isRequestedTabsData[templateId]) {
            setIsRequestedTabsData({ ...isRequestedTabsData, [templateId]: true });
            getTemplateInfo(templateId, pointInTime).then((data) => {
                if (deliveryMethod === TemplateTypesLabels[TemplateTypeEnum.PushNotification]) {
                    const { message_title: messageTitle } = JSON.parse(data.Body);
                    dataTabs[templateId] = { subject: messageTitle, body: data.Body, isBusy: false };
                } else {
                    dataTabs[templateId] = { subject: data.Subject, body: data.Body, isBusy: false };
                }
                setTabsData({ ...dataTabs });
            });
        }
    };

    const handleDeviceTabChange = (value: DeviceTabType) => {
        setActiveDeviceTab(value);
    };

    const handleDisplayModeChange = (value: DisplayModeEnum) => {
        setDisplayModeTab(value);
    };

    function getDefaultTemplateSubject() {
        return deliveryMethod === TemplateTypesLabels[TemplateTypeEnum.PushNotification]
            ? JSON.parse(defaultBody).message_title
            : defaultSubject;
    }

    function getDefaultTemplateBody() {
        return defaultBody;
    }

    const init = () => {
        setIsVisibleSubjectField(deliveryMethod !== TemplateTypesLabels[TemplateTypeEnum.SMS]);
        isEmpty(partnerLanguages) &&
            getPartnerLanguageList(dispatch, getPartnerLanguage.request).catch(ignoreCanceledError);

        if (deliveryMethod === TemplateTypesLabels[TemplateTypeEnum.DynamicPopUp]) {
            doGetRequest(getPopUpComparisons.request, {
                successCallback: (Data) => {
                    const popUpNotificationUrlComparisons = Data.map(({ Id, Name }) => ({ value: Id, label: Name }));
                    setUrlTypesData(popUpNotificationUrlComparisons);
                },
            });
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <div className="wrap-tabs-holder-info">
            <div className="wrap-tabs-holder-info-inner">
                {isVisibleDeviceTabs && (
                    <div className="inner-wrap-tabs-holder-info">
                        <div className="wrap-for-device">
                            <RadioTabGroup
                                activeTab={activeDeviceTab}
                                setActiveTab={handleDeviceTabChange}
                                tabs={DeviceTabInfos}
                            />
                        </div>
                    </div>
                )}
                <div className="display-mode-tabs-wrapper">
                    <RadioTabGroup
                        activeTab={displayModeTab}
                        setActiveTab={handleDisplayModeChange}
                        tabs={ModeTabInfos}
                    />
                </div>
            </div>
            <Tabs type="basic" position="top" fixedSize={false} onChange={handleTabChange}>
                <Tab
                    key={defaultTemplateId}
                    title={
                        <Tooltip text={t(l.Default)}>
                            <span>{t(l.Default)}</span>
                        </Tooltip>
                    }
                >
                    <TemplateInfoTab
                        isSubjectVisible={isVisibleSubjectField}
                        subject={getDefaultTemplateSubject()}
                        body={getDefaultTemplateBody()}
                        deliveryMethod={deliveryMethod}
                        configs={{
                            bodyConfig: DeviceTabInfos[activeDeviceTab],
                            modeConfig: ModeTabInfos[displayModeTab],
                            urlTypesData,
                        }}
                    />
                </Tab>

                {childItemsAdditionalInfo.map((childItem) => (
                    <Tab
                        key={childItem.TemplateId}
                        title={
                            <Tooltip text={childItem.tooltipText}>
                                <span>
                                    {activeLanguageTab === childItem.TemplateId
                                        ? childItem.languageName
                                        : childItem.languageCode}
                                </span>
                            </Tooltip>
                        }
                    >
                        <BusyLoader isBusy={tabsData[childItem.TemplateId].isBusy} type="spinner" spinnerSize="medium">
                            <TemplateInfoTab
                                isSubjectVisible={isVisibleSubjectField}
                                subject={tabsData[childItem.TemplateId].subject}
                                body={tabsData[childItem.TemplateId].body}
                                deliveryMethod={deliveryMethod}
                                configs={{
                                    bodyConfig: DeviceTabInfos[activeDeviceTab],
                                    modeConfig: ModeTabInfos[displayModeTab],
                                    urlTypesData,
                                }}
                            />
                        </BusyLoader>
                    </Tab>
                ))}
            </Tabs>
        </div>
    );
};

TemplateInfo.propTypes = {
    defaultTemplateId: PropTypes.number.isRequired,
    defaultBody: PropTypes.string.isRequired,
    defaultSubject: PropTypes.string.isRequired,
    getTemplateInfo: PropTypes.func.isRequired,
    childItemsInfo: PropTypes.array,
    deliveryMethod: PropTypes.number.isRequired,
};

TemplateInfo.defaultProps = {
    childItemsInfo: [],
};

export default memo(TemplateInfo);
